import { useMemo } from 'react'
import { isEmpty } from 'remeda'
import {
  type ApolloError,
  gql,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client'
import type { TransformedProductWithRawImages } from '@components/WishlistLineItem/transformProductToWishlistLineItem'
import type { UseProductsProductEdge, WishlistItem } from '@velasca/types'
import { transformProductsSliderData } from '@velasca/utils/transform-products-slider-data'
import type {
  WishlistProductsData,
  WishlistProductsVariables,
} from './Wishlist.types'

// Helper function to extract product handle from URL
const extractHandleFromUrl = (url: string): string | null => {
  if (!url) {
    return null
  }

  const cleanUrl = url.split('?')[0].split('#')[0]
  const match = cleanUrl.match(/\/products\/([^/]+)/)

  return match ? match[1] : null
}

const GET_WISHLIST_PRODUCTS: TypedDocumentNode<
  WishlistProductsData,
  WishlistProductsVariables
> = gql`
  query getWishlistProducts($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        isGiftCard
        id
        handle
        title
        productType
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        compareAtPriceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        media(first: 15) {
          edges {
            node {
              ... on MediaImage {
                image {
                  url
                }
              }
            }
          }
        }
        variants(first: 30) {
          edges {
            node {
              id
              quantityAvailable
              selectedOptions {
                name
                value
              }
            }
          }
        }
        color: metafield(namespace: "custom", key: "color") {
          value
        }
        colorShort: metafield(namespace: "custom", key: "color_short") {
          value
        }
        material: metafield(namespace: "custom", key: "material") {
          value
        }
        materialShort: metafield(namespace: "custom", key: "material_short") {
          value
        }
        reviewsCount: metafield(namespace: "stamped", key: "reviews_count") {
          value
        }
        reviewsAverage: metafield(
          namespace: "stamped"
          key: "reviews_average"
        ) {
          value
        }
        customProductType: metafield(namespace: "custom", key: "product_type") {
          value
        }
        frontImageIndex: metafield(
          namespace: "custom"
          key: "front_image_index"
        ) {
          value
        }
        hoverImageIndex: metafield(
          namespace: "custom"
          key: "hover_image_index"
        ) {
          value
        }
        swatchImageIndex: metafield(
          namespace: "custom"
          key: "swatch_image_index"
        ) {
          value
        }
        staffPickIconID: metafield(
          namespace: "custom"
          key: "staff_pick_icon_id"
        ) {
          value
        }
        isNew: metafield(namespace: "custom", key: "is_new") {
          value
        }
        collectionBrandName: metafield(
          namespace: "custom"
          key: "collection_brand_name"
        ) {
          value
        }
        collectionBrandID: metafield(
          namespace: "custom"
          key: "collection_brand_id"
        ) {
          value
        }
        seasonID: metafield(namespace: "custom", key: "season_id") {
          value
        }
        seasonName: metafield(namespace: "custom", key: "season_name") {
          value
        }
        sole: metafield(namespace: "custom", key: "sole") {
          value
        }
      }
    }
  }
`

/**
 * Hook to fetch product data from Shopify based on Swym wishlist items.
 * This provides rich product data that the ApparelProduct component needs.
 *
 * @param wishlistItems - Array of Swym ListItem objects.
 * @param skip - Whether to skip the query (default: false).
 * @returns Object containing loading state, transformed products, and error.
 */
export const useWishlistProducts = (
  wishlistItems: WishlistItem[] = [],
  skip = false,
): {
  loading: boolean
  wishlistProducts: TransformedProductWithRawImages[]
  error: ApolloError | undefined
} => {
  // Extract unique product IDs from wishlist items
  const productIds = useMemo((): string[] => {
    if (!Array.isArray(wishlistItems)) {
      return []
    }

    const ids = new Set<string>()

    wishlistItems.forEach((item) => {
      // Extract product ID from empi (external Shopify product ID)
      if (item.empi) {
        // Convert to Shopify GID format
        const shopifyId = `gid://shopify/Product/${item.empi}`

        ids.add(shopifyId)
      }
    })

    return [...ids]
  }, [wishlistItems])

  const { data, loading, error } = useQuery(GET_WISHLIST_PRODUCTS, {
    variables: { ids: productIds },
    skip: skip || isEmpty(productIds),
    fetchPolicy: 'cache-first',
  })

  // Create a mapping of product handles to their creation timestamps for sorting
  const handleToTimestamp = useMemo(() => {
    const mapping = new Map<string, number>()

    wishlistItems.forEach((item) => {
      const handle = extractHandleFromUrl(item.du)

      if (handle) {
        mapping.set(handle, item.cts)
      }
    })

    return mapping
  }, [wishlistItems])

  // Create enhanced product data for wishlist components
  const wishlistProducts = useMemo(() => {
    if (!data?.nodes) {
      return []
    }

    // Filter out null nodes and ensure type safety
    const validProducts = data.nodes.filter(Boolean)

    const enhancedProducts = validProducts
      .map((product) => {
        if (!product) {
          return null
        }

        // Convert to the format expected by transformProductsSliderData
        // Note: Our WishlistProduct has similar structure to UseProductsProduct but with
        // different metafield types. The transformation function handles this gracefully.
        const productEdge: UseProductsProductEdge = {
          node: product as unknown as UseProductsProductEdge['node'],
          cursor: '', // Required by the type but not used in transformation
        }

        // Get the base transformed product (we need this for the variants, sizes, etc.)
        const transformedProduct = transformProductsSliderData([productEdge])[0]

        // Extract raw images from media for proper image handling - simplified approach
        const rawImages: { src: string; alt: string }[] = []

        try {
          const mediaEdges = product.media?.edges || []

          for (const edge of mediaEdges) {
            const node = edge?.node

            if (node?.__typename === 'MediaImage' && node?.image?.url) {
              rawImages.push({ src: node.image.url, alt: product.title })
            }
          }
        } catch (error) {
          // Fallback to empty array if media processing fails
          console.warn('Failed to process media:', error)
        }

        // Get the creation timestamp for this product
        const creationTimestamp = handleToTimestamp.get(product.handle) ?? 0

        return {
          ...transformedProduct,
          rawImages,
          customProductType: product.customProductType?.value ?? undefined,
          frontImageIndex: product.frontImageIndex?.value ?? undefined,
          creationTimestamp,
        }
      })
      .filter(
        (product): product is NonNullable<typeof product> => product !== null,
      )

    // Sort by creation timestamp - most recent first (highest timestamp first)
    return enhancedProducts.sort(
      (a, b) => b.creationTimestamp - a.creationTimestamp,
    )
  }, [data?.nodes, handleToTimestamp])

  return {
    loading,
    wishlistProducts,
    error,
  }
}
